import { useState, useEffect, useRef } from 'react';
import { MessageCircle, MapPin, Phone, Mail, Clock, X, MessageSquarePlus } from 'lucide-react';
import { useContactForm } from '../../contexts/ContactFormContext';

const FloatingContactWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [noteDismissed, setNoteDismissed] = useState(false);
  const { openForm } = useContactForm();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (noteDismissed || isOpen) {
      setShowNote(false);
      return;
    }

    const scheduleNextShow = (delay: number) => {
      timerRef.current = setTimeout(() => {
        setShowNote(true);
        // Auto-hide after 16 seconds (longer duration)
        hideTimerRef.current = setTimeout(() => {
          setShowNote(false);
          // Schedule next show at random interval between 25-45 seconds
          const nextInterval = Math.floor(Math.random() * (45000 - 25000 + 1)) + 25000;
          scheduleNextShow(nextInterval);
        }, 16000);
      }, delay);
    };

    // First popup appears after 4 seconds
    scheduleNextShow(4000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [noteDismissed, isOpen]);

  const handleNoteClick = () => {
    setShowNote(false);
    setNoteDismissed(true);
    openForm();
  };

  const handleDismissNote = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowNote(false);
    setNoteDismissed(true);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end pointer-events-none">
      {/* Expanded Widget */}
      <div 
        className={`bg-white text-black shadow-2xl rounded-none overflow-hidden transition-all duration-300 origin-bottom-right mb-4 border border-foreground/10 ${
          isOpen ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto visible' : 'opacity-0 scale-95 translate-y-4 pointer-events-none invisible'
        }`}
        style={{ width: '320px' }}
      >
        <div className="bg-primary/10 p-5 pb-4 border-b border-foreground/10 relative">
          <h3 className="font-bold text-lg mb-1">We're here to help!</h3>
          <p className="text-sm text-foreground/70">Reach out directly or send a message.</p>
          <button 
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 p-1 hover:bg-foreground/10 rounded-full transition-colors"
            aria-label="Close help widget"
          >
            <X size={18} />
          </button>
        </div>
        
        <div className="p-5 space-y-4">
          <div className="flex flex-col gap-4 text-sm">
            <div className="flex items-start gap-3">
              <Phone size={18} className="text-primary mt-0.5" />
              <div>
                <a href="tel:+919774477416" className="font-semibold hover:text-primary transition-colors block">+91 9774477416</a>
                <span className="text-xs text-foreground/60">Call / Text / WA (24/7)</span>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Mail size={18} className="text-primary mt-0.5" />
              <div>
                <a href="mailto:hello@paokhum.com" className="font-semibold hover:text-primary transition-colors block">hello@paokhum.com</a>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock size={18} className="text-primary mt-0.5" />
              <div>
                <span className="font-semibold block">Mon-Fri: 10AM - 5PM</span>
                <span className="text-xs text-foreground/60">Phone Support hours</span>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-primary mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold block">Paokhum</span>
                <span className="text-xs text-foreground/60 block">Chingarel Tejpur, Imphal East, Manipur 795010</span>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => {
              setIsOpen(false);
              openForm();
            }}
            className="w-full mt-2 bg-black text-white py-3 px-4 rounded-none font-semibold flex items-center justify-center gap-2 hover:bg-black/80 transition-colors shadow-sm cursor-pointer"
          >
            <MessageSquarePlus size={18} />
            Send a Message
          </button>
        </div>
      </div>

      {/* Button & Side-by-Side Floating Consultation Note */}
      <div className="flex items-center gap-3">
        {/* Floating Consultation Note (Positioned to the left of the button) */}
        <div 
          className={`transition-all duration-500 ease-out transform origin-right ${
            showNote && !isOpen
              ? 'opacity-100 translate-x-0 scale-100 pointer-events-auto visible'
              : 'opacity-0 translate-x-4 scale-95 pointer-events-none invisible'
          }`}
        >
          <div 
            onClick={handleNoteClick}
            className="relative bg-black text-white dark:bg-white dark:text-black py-3 px-4 rounded-2xl shadow-2xl border border-foreground/15 max-w-[260px] sm:max-w-[300px] cursor-pointer group hover:scale-[1.02] transition-all duration-200 flex items-center justify-between gap-3"
          >
            {/* Live Status Pulse Dot (Replaces generic star icon) */}
            <div className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold leading-tight tracking-tight">
                Have questions or custom ideas?
              </p>
              <p className="text-[11px] opacity-80 mt-0.5 flex items-center gap-1 font-medium text-emerald-400 dark:text-emerald-600">
                Book a free consultation <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
              </p>
            </div>

            <button
              onClick={handleDismissNote}
              className="bg-white/10 dark:bg-black/10 hover:bg-white/20 dark:hover:bg-black/20 rounded-full p-1 transition-colors cursor-pointer shrink-0"
              aria-label="Dismiss note"
            >
              <X size={12} />
            </button>
          </div>
        </div>

        {/* Floating Toggle Button */}
        <button
          onClick={() => {
            setIsOpen(!isOpen);
            setShowNote(false);
          }}
          className="pointer-events-auto w-14 h-14 bg-black group text-white rounded-full shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 hover:bg-black/90 hover:shadow-[0_8px_32px_rgba(0,224,255,0.3)] shrink-0 cursor-pointer"
          aria-label="Toggle help widget"
        >
          {isOpen ? (
            <X className="w-6 h-6 transition-colors duration-300 group-hover:text-primary" />
          ) : (
            <MessageCircle className="w-6 h-6 transition-colors duration-300 group-hover:text-primary" />
          )}
        </button>
      </div>
    </div>
  );
};

export default FloatingContactWidget;
